import React, { forwardRef, useImperativeHandle, useEffect, useState, useMemo, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { isEmpty, isNil, is, head, pathOr, pluck } from 'ramda';
import { klona } from 'klona';
import { wrap } from 'object-path-immutable';

// components
import FormField from '../../../../components/FormField';
import BandoEditFormActions from '../BandoEditFormActions';
import UnsavedChangesDetector from '../../../../components/UnsavedChangesDetector';
import { Toast } from 'primereact/toast';

// api
import BandoService from '../../../../service/bando-service';
import LookupdataService from '../../../../service/lookupdata-service';

// store
import { storeSet } from '../../../../store';

// tools
import set404FromErrorResponse from '../../../../helpers/set404FromErrorResponse';
import formatDateString from '../../../../helpers/formatDateString';

// api
import EvaluationFormsService from '../../../../service/evaluation-forms-service';

const BandoEditFormStep1 = forwardRef(function ({ initialData, setInitialData, getFormErrors, status }, ref) {
    const navigate = useNavigate();
    const [aimedToOptions, setAimedToOptions] = useState([]);
    const [formInitialData, setFormInitialData] = useState(initialData);
    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        setValue,
        trigger,
        getValues,
        clearErrors
    } = useForm({
        defaultValues: useMemo(() => {
            return formInitialData;
        }, [formInitialData]), mode: 'onChange'
    });
    const values = getValues();
    const toast = useRef(null);

    const onSubmit = () => {
    };

    const onSaveDraft = () => {
        trigger();
        let formData = klona(getValues());
        formData.dates = [];

        if (formData.startDate) {
            let starDate;

            if (is(String, formData.startDate)) {
                starDate = formData.startDate;
            } else {
                starDate = formatDateString(formData.startDate);
            }

            formData = wrap(formData).insert(['dates'], starDate, 0).value();
        }
        if (formData.endDate) {
            let endDate;

            if (is(String, formData.endDate)) {
                endDate = formData.endDate;
            } else {
                endDate = formatDateString(formData.endDate);
            }

            formData = wrap(formData).insert(['dates'], endDate, 1).value();
        }
        if (formData.aimedTo) {
            formData.aimedTo = aimedToOptions.filter(o => o.lookUpDataId === formData.aimedTo);
        }

        delete formData.startDate;
        delete formData.endDate;

        formData = {
            ...formData,
            confidi: true
        }

        storeSet('setAsyncRequest');

        if (!formData.id) {
            BandoService.createBando(formData, createCallback, errCreateCallback);
        } else {
            BandoService.updateBandoStep1(formData.id, formData, createCallback, errCreateCallback);
        }
    }

    const createCallback = (data) => {
        storeSet('unsetAsyncRequest');
        if (data.status === 'SUCCESS') {
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: __('Il form è stato aggiornato correttamente!', 'htr')
                });
            }
            const values = getValues();
            if (!values.id && data.data.id) {
                storeSet('setAsyncRequest');
                const sampleFormData = {
                    label: `Evaluation form for call #${data.data.id}`,
                    content: []
                }
                EvaluationFormsService.createFormForCall(data.data.id, sampleFormData, createFormCallback, errCreateFormCallback)
            } else {
                setFormInitialData(data.data);
                setInitialData(data.data);
            }
        }
    }

    const errCreateCallback = (data) => {
        if (toast.current && data.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: data.message
            });
        }
        set404FromErrorResponse(data);
        storeSet('unsetAsyncRequest');
    }

    const createFormCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            navigate(`/moduli/${resp.data.callId}`);
        }
        storeSet('unsetAsyncRequest');
    }

    const errCreateFormCallback = (resp) => {
        set404FromErrorResponse(resp);
        storeSet('unsetAsyncRequest');
    }

    const openPreview = () => {
        navigate(`/moduli/${values.id}/preview`);
    }

    const openPreviewEvaluation = () => {
        navigate(`/moduli/${values.id}/preview-evaluation`);
    }

    const lookupdataCallback = (data) => {
        if (data.status === 'SUCCESS') {
            const aimedTo = data.data
                .filter(o => o.type === 'AIMED_TO')
                .map(o => {
                    delete o.type;
                    return {
                        ...o,
                        lookUpDataId: o.id,
                        id: null
                    };
                });
            setAimedToOptions(aimedTo);
        }
        storeSet('unsetAsyncRequest');
    }

    const errLookupdataCallback = (data) => {
        storeSet('unsetAsyncRequest');
    }

    const shouldDisableField = (fieldName) => {
        return values.status === 'PUBLISH' &&
            ![
                'descriptionShort', 'descriptionLong', 'documentationRequested', 'threshold',
                'aimedTo', 'criteria', 'docs', 'checklist', 'faq', 'amount', 'amountMin', 'amountMax',
                'email', 'phoneNumber', 'checkList', 'images', 'numberOfCheck', 'appointmentTemplateId',
                'endDate', 'endTime'
            ].includes(fieldName)
    }

    useImperativeHandle(
        ref,
        () => {
            return {
                isFormValid: () => {
                    return isValid;
                },
                getErrors: () => {
                    return errors;
                },
                getValues: () => {
                    return getValues();
                }
            }
        }, [errors, isValid]);

    useEffect(() => {
        storeSet('formInitialData', initialData);
        setFormInitialData(initialData);
    }, [initialData]);

    useEffect(() => {
        const newFormData = klona(formInitialData);
        if (!isNil(formInitialData.dates) && formInitialData.dates.length) {
            if (newFormData.dates[0]) {
                newFormData.startDate = is(String, newFormData.dates[0]) ? new Date(newFormData.dates[0]) : (newFormData.dates[0] ? newFormData.dates[0] : '');
            }
            if (newFormData.dates[1]) {
                newFormData.endDate = is(String, newFormData.dates[1]) ? new Date(newFormData.dates[1]) : (newFormData.dates[1] ? newFormData.dates[1] : '');
            }
        }
        if (!isNil(formInitialData.startTime) && !isEmpty(formInitialData.startTime)) {
            newFormData.startTime = null;
        }
        if (!isNil(formInitialData.endTime) && !isEmpty(formInitialData.endTime)) {
            newFormData.endTime = null;
        }
        if (newFormData.aimedTo) {
            newFormData.aimedTo = pathOr(0, [], head(pluck('lookUpDataId', newFormData.aimedTo)));
        }

        Object.keys(newFormData).map(v => setValue(v, newFormData[v]));
    }, [formInitialData]);

    useEffect(() => {
        trigger().then(() => clearErrors());
        LookupdataService.getItems(lookupdataCallback, errLookupdataCallback, [['type', ['AIMED_TO', 'FAQ']]]);

        return () => {
            storeSet('formInitialData', {});
        }
    }, []);

    return (
        <form className="appForm" onSubmit={handleSubmit(onSubmit)}>
            <UnsavedChangesDetector getValuesFn={getValues}/>
            <FormField
                type="textinput"
                disabled={shouldDisableField('name')}
                fieldName="name"
                label={__('Titolo del Form', 'htr')}
                control={control}
                errors={errors}
                defaultValue={values['name']}
                config={{ required: __('È obbligatorio', 'htr') }}
            />

            <FormField
                type="wysiwyg"
                disabled={shouldDisableField('descriptionShort')}
                fieldName="descriptionShort"
                label={__('Descrizione breve', 'htr')}
                control={control}
                errors={errors}
                defaultValue={values['descriptionShort']}
                config={{
                    required: __('È obbligatorio', 'htr')
                }}
            />

            <FormField
                type="wysiwyg"
                disabled={shouldDisableField('descriptionLong')}
                fieldName="descriptionLong"
                label={__('Descrizione completa', 'htr')}
                control={control}
                rows={7}
                errors={errors}
                defaultValue={values['descriptionLong']}
                config={{
                    required: __('È obbligatorio', 'htr'),
                    minLength: 200
                }}
                infoText={__('Almeno 200 caratteri', 'htr')}
            />

            <div className="appForm__cols">
                <FormField
                    type="chips"
                    fieldName="aimedTo"
                    label={__('Seleziona la categoria', 'htr')}
                    control={control}
                    errors={errors}
                    multiple={true}
                    config={{
                        required: true,
                        validate: value => (Array.isArray(value) ? value.length > 0 : value > 0) || __('Questo campo è obbligatorio', 'htr')
                    }}
                    lookupType="AIMED_TO"
                />
            </div>

            <FormField
                type="wysiwyg"
                disabled={shouldDisableField('documentationRequested')}
                fieldName="documentationRequested"
                label={__('Documentazione richiesta', 'htr')}
                control={control}
                errors={errors}
                defaultValue={values['documentationRequested']}
                config={{
                    required: __('È obbligatorio', 'htr')
                }}
            />

            <div className="appForm__cols">
                <FormField
                    type="datepicker"
                    disabled={shouldDisableField('startDate')}
                    fieldName="startDate"
                    label={__('Data di inizio', 'htr')}
                    control={control}
                    errors={errors}
                    defaultValue={values['startDate']}
                    config={{ required: __('È obbligatorio', 'htr') }}
                />

                <FormField
                    type="datepicker"
                    disabled={shouldDisableField('endDate')}
                    fieldName="endDate"
                    label={__('Data di fine', 'htr')}
                    control={control}
                    errors={errors}
                    defaultValue={values['endDate']}
                    config={{ required: __('È obbligatorio', 'htr') }}
                />
            </div>

            {/*<FormFieldRepeaterFaq
                data={values['faq']}
                disabled={shouldDisableField('faq')}
                setDataFn={setValue}
                fieldName="faq"
                options={faqOptions}
                errors={errors}
                register={register}
                trigger={trigger}
                config={{
                    validate: {
                        noEmptyValue: v => v
                            .filter(o => isEmpty(o.question) || isEmpty(o.answer)).length === 0 || __('Non lasciare il valore vuoto', 'htr')
                    }
                }}
                label={__('FAQ', 'htr')}/>*/}

            <div className="appPage__spacer"></div>

            <div className="appPageSection__hr">
                <span>{__('Azioni', 'htr')}</span>
            </div>

            <Toast ref={toast}/>
            <BandoEditFormActions
                id={values.id}
                status={status}
                submitFn={onSaveDraft}
                openPreview={openPreview}
                openPreviewEvaluation={openPreviewEvaluation}/>
        </form>
    )
})

export default BandoEditFormStep1;